// ForgotPassword.tsx
import React, { useState } from 'react';
import { Video, Mail, ArrowRight, ArrowLeft, CheckCircle, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { authService } from '../services/auth.service.ts';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmed = email.trim();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    setIsLoading(true);
    try {
      await authService.forgotPassword(trimmed);
      setIsSubmitted(true);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 429) {
        setError('Çok fazla deneme yapıldı. Lütfen bir süre sonra tekrar deneyin.');
      } else {
        setError(
          err?.response?.data?.error ||
            err?.message ||
            'İstek gönderilemedi. Lütfen tekrar deneyin.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4 overflow-hidden">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-purple-500/30 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-blue-500/30 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse"></div>
      </div>

      <div className="relative w-full max-w-md">
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl blur opacity-30"></div>
        <div className="relative bg-slate-800/80 backdrop-blur-xl rounded-2xl border border-white/10 p-6">
          
          <Link to="/login" className="inline-flex items-center space-x-2 text-gray-400 hover:text-white transition mb-4 group">
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition" />
            <span>Giriş Sayfasına Dön</span>
          </Link>

          <div className="flex items-center justify-center space-x-2 mb-4">
            <Video className="w-8 h-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">TBKMeet</span>
          </div>

          {!isSubmitted ? (
            <>
              <h2 className="text-xl font-bold text-white text-center mb-1">Şifremi Unuttum</h2>
              <p className="text-gray-400 text-center text-sm mb-6">
                E-posta adresinize şifre sıfırlama bağlantısı göndereceğiz
              </p>

              {error && (
                <div
                  role="alert"
                  className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm"
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    E-posta Adresi
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ornek@email.com"
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition text-sm"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Gönderiliyor...</span>
                    </>
                  ) : (
                    <>
                      <span>Şifre Sıfırlama Bağlantısı Gönder</span>
                      <Send className="w-5 h-5" />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-4">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
              </div>
              
              <h2 className="text-xl font-bold text-white mb-2">E-posta Gönderildi!</h2>
              <p className="text-gray-400 text-sm mb-2">
                <span className="text-white font-medium">{email}</span> adresine şifre sıfırlama bağlantısı gönderildi.
              </p>
              <p className="text-gray-500 text-xs">
                E-postanızı kontrol edin ve gelen bağlantıya tıklayarak yeni şifrenizi oluşturun.
              </p>

              <div className="pt-4 space-y-3">
                <button
                  onClick={() => {
                    setIsSubmitted(false);
                    setEmail('');
                    setError('');
                  }}
                  className="w-full py-2.5 bg-white/5 border border-white/10 text-white rounded-lg font-semibold hover:bg-white/10 transition"
                >
                  Yeni E-posta Gönder
                </button>
                
                <Link
                  to="/login"
                  className="block w-full py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold transition transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25 flex items-center justify-center space-x-2"
                >
                  <span>Giriş Sayfasına Dön</span>
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t border-white/10">
            <p className="text-center text-gray-400 text-sm">
              Hesabınız yok mu?{' '}
              <Link to="/register" className="text-purple-400 hover:text-purple-300 font-semibold transition">
                Kayıt Ol
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
